/*
 * Workpad notes are rich text, produced by `document.execCommand` inside a
 * contenteditable and rendered back through `dangerouslySetInnerHTML`. Today
 * that round-trip is self-inflicted — the HTML comes from your own keyboard and
 * never leaves your own localStorage — so the worst you can do is attack
 * yourself.
 *
 * It stops being self-inflicted the moment `notesService` becomes the endpoint
 * the integration doc describes. Notes then sync between devices and, if notes
 * are ever shared or an admin view ever renders them, between people; at that
 * point an unsanitised `dangerouslySetInnerHTML` is stored XSS. Doing this while
 * the blast radius is one browser is much cheaper than doing it afterwards.
 *
 * An allowlist rather than DOMPurify because the input is not arbitrary HTML:
 * execCommand emits a small, known set of tags and exactly three style
 * properties. A dependency for that is not yet earned — but if the workpad ever
 * grows paste-from-Word, embedded media, or anything else that widens the input,
 * take the dependency rather than widening these lists.
 */

/** Tags execCommand produces, plus the block wrappers contenteditable inserts. */
const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'BR', 'DIV', 'P', 'SPAN', 'FONT', 'UL', 'OL', 'LI',
  /* BLOCKQUOTE is what `execCommand('indent')` writes — it is not a quotation
     here, it is the only element Chrome has for "one level in". Its absence from
     this list meant Indent and Outdent were toolbar buttons that did nothing:
     the indent appeared while you typed and was unwrapped by this function the
     moment the note blurred and saved. Two buttons that lie about what they do
     are worse than two buttons that are not there. */
  'BLOCKQUOTE',
]);

/*
 * How many levels of indent survive a save.
 *
 * The workpad column is ~258px. Nothing bounds how many times Indent can be
 * pressed, and each level costs the text width it is worth — past three the
 * prose is a column of single words, and `overflow-wrap: anywhere` means it
 * degrades into one character per line rather than announcing itself by
 * overflowing. Capping in the sanitiser rather than in CSS bounds the stored
 * note as well as the drawn one, which is the difference between a rendering
 * quirk and a note nobody can read on the next device they open it on.
 */
const MAX_INDENT_DEPTH = 3;

/** Blockquote ancestors between `el` and the note body. */
function indentDepth(el: Element, root: Element): number {
  let depth = 0;
  for (let node = el.parentElement; node && node !== root; node = node.parentElement) {
    if (node.tagName.toUpperCase() === 'BLOCKQUOTE') depth += 1;
  }
  return depth;
}

/** Dropped whole, contents and all — unwrapping these would surface their source. */
const DROP_ENTIRELY = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META']);

/** `foreColor` writes `<font color>`; nothing else earns an attribute. */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  FONT: new Set(['color']),
};

/** `hiliteColor` and the justify commands write these three, and only these. */
const ALLOWED_STYLE_PROPS = new Set(['color', 'background-color', 'text-align']);

/*
 * Colours and keywords only. The explicit rejections matter more than the
 * allowlist: `url(...)` can fetch, and the others are legacy script vectors that
 * some engines still honour in a style attribute.
 */
const SAFE_STYLE_VALUE = /^[#\w(),.%\s-]+$/;
const UNSAFE_STYLE_VALUE = /url\s*\(|expression\s*\(|javascript:|@import|behaviou?r:/i;

function safeStyle(style: string): string {
  return style
    .split(';')
    .map((decl) => {
      const [rawProp, ...rest] = decl.split(':');
      const prop = rawProp?.trim().toLowerCase();
      const value = rest.join(':').trim();
      if (!prop || !value) return null;
      if (!ALLOWED_STYLE_PROPS.has(prop)) return null;
      if (!SAFE_STYLE_VALUE.test(value) || UNSAFE_STYLE_VALUE.test(value)) return null;
      return `${prop}: ${value}`;
    })
    .filter((decl): decl is string => decl !== null)
    .join('; ');
}

/**
 * Strips anything the workpad's own toolbar could not have produced.
 *
 * Disallowed *tags* are unwrapped rather than removed — a note wrapped in a
 * stray `<article>` should lose the tag, not the sentence — but the elements in
 * `DROP_ENTIRELY` go completely, since their text content is source code rather
 * than prose.
 */
export function sanitizeNoteHtml(html: string): string {
  if (!html) return '';
  // No DOMParser outside a browser; refusing to render is the safe direction.
  if (typeof DOMParser === 'undefined') return '';

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

  // Snapshot first: unwrapping mutates the tree, and a live list would skip nodes.
  for (const el of Array.from(doc.body.querySelectorAll('*'))) {
    /* Upper-cased rather than trusted, because `tagName` only reports uppercase
       for HTML elements. Inside foreign content — `<svg>`, `<math>` — it comes
       back exactly as authored, so `<svg><script>` reported 'script' and missed
       an all-uppercase set. It then failed the allowlist too and took the
       *unwrap* branch, which surfaced the script's source as note prose: the one
       thing DROP_ENTIRELY exists to stop. Not an XSS — the text is serialised
       escaped — but the sanitiser was quietly not doing its job. */
    const tag = el.tagName.toUpperCase();
    if (DROP_ENTIRELY.has(tag)) {
      el.remove();
      continue;
    }
    if (!ALLOWED_TAGS.has(tag)) {
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }
    /* Unwrapping the *deepest* levels rather than the outermost keeps the
       indent the user can see and drops only the ones past what the column can
       carry. `querySelectorAll` is in document order, so an outer blockquote is
       always decided before the ones inside it — which is what makes the
       ancestor count below reliable while the tree is being rewritten. */
    if (tag === 'BLOCKQUOTE' && indentDepth(el, doc.body) >= MAX_INDENT_DEPTH) {
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name === 'style') {
        const cleaned = safeStyle(attr.value);
        if (cleaned) el.setAttribute('style', cleaned);
        else el.removeAttribute('style');
        continue;
      }
      if (!ALLOWED_ATTRS[tag]?.has(name)) el.removeAttribute(attr.name);
    }
  }

  return doc.body.innerHTML;
}
